import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle, Package, Printer, Truck, MapPin, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import SEO from '../components/SEO';
import { trackPurchase } from '../lib/analytics';
import { initTagadaThankYou, destroyTagada, initTagada } from '../lib/tagadaTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_peptide-research-hub-6/artifacts/kowhmc7f_Gemini_Generated_Image_tlwfymtlwfymtlwf__1_-removebg.png";

const money = (n) => `$${Number(n || 0).toFixed(2)} USD`;

function formatAddress(addr) {
  if (!addr) return null;
  const lines = [
    addr.name || addr.full_name,
    addr.address_line_1 || addr.address || addr.line1,
    addr.address_line_2 || addr.line2,
    [addr.city, addr.state, addr.zip || addr.postal_code].filter(Boolean).join(', '),
    addr.country,
  ].filter(Boolean);
  return lines;
}

function OrderHeader({ orderNumber, paidAt, status, manualPending }) {
  const dateStr = paidAt
    ? new Date(paidAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';
  const statusLabel = manualPending ? 'Payment pending' : (status || 'paid');
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-6 border-b border-slate-200">
      <div className="flex items-center gap-4">
        <img src={LOGO} alt="Wilvada LC" className="h-14 w-auto object-contain" />
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">Wilvada LC</p>
          <p className="text-xs text-slate-400 font-body">Research-Use-Only</p>
        </div>
      </div>
      <div className="text-left sm:text-right">
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">Order</p>
        <p className="text-2xl font-heading text-slate-900" data-testid="invoice-order-number">#{orderNumber || '—'}</p>
        <p className="text-xs text-slate-500 font-body mt-1">{dateStr} · <span className="capitalize">{statusLabel}</span></p>
      </div>
    </div>
  );
}

function AddressBlock({ title, address, icon: Icon }) {
  const lines = formatAddress(address);
  if (!lines || lines.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {title}
      </div>
      {lines.map((line, i) => (
        <p key={`addr-${i}-${(line || '').slice(0, 12)}`} className="text-sm text-slate-700 font-body leading-snug">{line}</p>
      ))}
    </div>
  );
}

function ItemsTable({ items }) {
  return (
    <div className="overflow-hidden border border-slate-200 rounded-md">
      <table className="w-full text-sm font-body" data-testid="invoice-items-table">
        <thead className="bg-slate-50/80">
          <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-slate-500">
            <th className="py-3 px-3 sm:px-4 font-medium">Item</th>
            <th className="py-3 px-2 font-medium text-center w-16">Qty</th>
            <th className="py-3 px-2 font-medium text-right w-20 hidden sm:table-cell">Price</th>
            <th className="py-3 px-3 sm:px-4 font-medium text-right w-24">Total</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((it, i) => (
            <tr key={`${it.product_id || 'row'}-${it.variant || ''}-${i}`} className="border-t border-slate-200 align-top">
              <td className="py-3 px-3 sm:px-4">
                <div className="flex items-center gap-3">
                  {it.image && (
                    <img
                      src={it.image}
                      alt={it.name}
                      className="h-12 w-12 rounded-md object-contain bg-slate-50 border border-slate-200 flex-shrink-0 print:h-10 print:w-10"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-slate-900 font-medium leading-tight">{it.name}</p>
                    {it.variant && (
                      <p className="text-xs text-slate-500 mt-0.5">{it.variant}</p>
                    )}
                    {it.bulk_discount_percent > 0 && (
                      <p className="text-[10px] uppercase tracking-wider text-emerald-700 mt-0.5">
                        Bulk −{it.bulk_discount_percent}%
                      </p>
                    )}
                  </div>
                </div>
              </td>
              <td className="py-3 px-2 text-center text-slate-700">{it.quantity}</td>
              <td className="py-3 px-2 text-right text-slate-700 hidden sm:table-cell">{money(it.price)}</td>
              <td className="py-3 px-3 sm:px-4 text-right text-slate-900 font-medium">{money(it.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TotalsBlock({ tx, manualPending = false }) {
  const subtotal = tx.subtotal ?? tx.original_amount ?? 0;
  const discount = tx.discount || 0;
  const shipping = tx.shipping_cost || 0;
  const total = tx.amount ?? tx.total ?? 0;
  return (
    <div className="space-y-1.5 text-sm font-body" data-testid="invoice-totals">
      <Row label="Subtotal" value={money(subtotal)} />
      {discount > 0 && (
        <Row
          label={tx.promo_code ? `Discount (${tx.promo_code})` : 'Discount'}
          value={`−${money(discount)}`}
          accent="text-emerald-700"
        />
      )}
      <Row
        label={tx.shipping_label || 'Shipping'}
        value={shipping > 0 ? money(shipping) : 'FREE'}
        accent={shipping === 0 ? 'text-emerald-700' : ''}
      />
      <Separator className="my-2" />
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">
          {manualPending ? 'Total Due' : 'Total Charged'}
        </span>
        <span className="text-xl text-slate-900 font-heading" data-testid="invoice-total">{money(total)}</span>
      </div>
    </div>
  );
}

function Row({ label, value, accent = '' }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-slate-600">{label}</span>
      <span className={`text-slate-900 font-medium ${accent}`}>{value}</span>
    </div>
  );
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  // Accept any of: session_id (our internal cart UUID), checkoutToken
  // (TagadaPay's hash), or orderId / order_id / cartToken — so a single
  // URL `https://wilvada.com/payment/success` can be configured as the
  // default returnUrl in TagadaPay's dashboard regardless of which token
  // their funnel appends.
  const sessionId = searchParams.get('session_id');
  const altToken =
    searchParams.get('checkoutToken') ||
    searchParams.get('cartToken') ||
    searchParams.get('orderId') ||
    searchParams.get('order_id') ||
    searchParams.get('orderNumber') ||
    searchParams.get('token');
  // Manual Checkout Mode marker — the checkout backend appends `&manual=1`
  // when no TagadaPay session was created. We treat the transaction as
  // immediately "viewable" (no polling for paid status) and render a
  // copy variant that says the payment link is on its way.
  const isManual = searchParams.get('manual') === '1';
  const [status, setStatus] = useState('loading');
  const [transaction, setTransaction] = useState(null);
  const purchaseTracked = useRef(false);

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId && !altToken) { setStatus('error'); return; }
    const maxAttempts = 30;
    if (attempts >= maxAttempts) { setStatus('timeout'); return; }
    try {
      const endpoint = sessionId
        ? `${API}/checkout/status/${sessionId}`
        : `${API}/checkout/by-token/${altToken}`;
      const { data } = await axios.get(endpoint, { withCredentials: true });
      setTransaction(data);
      // Manual mode never reaches "paid" via TagadaPay polling — payment
      // is collected out-of-band by the team. Surface the receipt right away.
      if (isManual || data.payment_provider === 'manual') {
        setStatus('manual_pending');
        return;
      }
      if (data.payment_status === 'paid') {
        setStatus('success');
        if (!purchaseTracked.current) {
          purchaseTracked.current = true;
          trackPurchase({
            orderId: data.tagada_order_number || data.session_id || sessionId || altToken,
            value: data.amount || 0,
            items: data.items || [],
          });
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setTimeout(() => pollStatus(attempts + 1), 2000);
    } catch (err) {
      // Hard-stop on 401 — the customer was redirected to the success page
      // without a Wilvada session cookie (anonymous browser, or session
      // expired during the TagadaPay checkout). Show a Sign-In CTA rather
      // than spinning forever.
      if (err?.response?.status === 401) {
        setStatus('signin');
        return;
      }
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId, altToken, isManual]);

  useEffect(() => { pollStatus(); }, [pollStatus]);

  // Re-init the TagadaPay tracker for the Thank You funnel step. Destroys
  // the storefront tracker on mount and restores it on unmount.
  useEffect(() => {
    initTagadaThankYou();
    return () => {
      destroyTagada();
      initTagada();
    };
  }, []);

  // ── Loading ──
  if (status === 'loading') {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
        <SEO title="Processing Your Order" noIndex />
        <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-6" />
        <h2 className="text-2xl font-heading text-foreground mb-2">Confirming your payment</h2>
        <p className="text-muted-foreground font-body">
          This usually takes just a few seconds. Please don't close this window.
        </p>
      </div>
    );
  }

  // ── Sign-in required (401 from /checkout/by-token) ──
  if (status === 'signin') {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
        <SEO title="Sign In to View Your Order" noIndex />
        <Card>
          <CardContent className="py-12 space-y-6" data-testid="payment-signin-required">
            <CheckCircle className="h-14 w-14 text-emerald-600 mx-auto" />
            <div>
              <h2 className="text-2xl font-heading text-foreground mb-2">Your order is being processed</h2>
              <p className="text-muted-foreground font-body mb-6">
                Please sign in to view the receipt and order details. We've also emailed
                you a copy of the invoice.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild data-testid="signin-button">
                  <Link to={`/login?next=${next}`}>Sign In</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/catalog">Continue Shopping</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Failed / Timeout ──
  if (status === 'error' || status === 'expired' || status === 'timeout') {
    const title = status === 'timeout'
      ? "We couldn't confirm your order"
      : status === 'expired'
      ? 'Checkout session expired'
      : 'Payment issue';
    const body = status === 'timeout'
      ? "Don't worry — if your payment went through, you'll receive a confirmation email shortly. You can also check your dashboard."
      : status === 'expired'
      ? 'Your checkout session has expired. Please add items to your cart and try again.'
      : 'There was an issue completing your payment. Please try again or contact support.';
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
        <SEO title="Order Status" noIndex />
        <Card>
          <CardContent className="py-12 space-y-6">
            <div data-testid="payment-failed">
              <XCircle className="h-14 w-14 text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-heading text-foreground mb-2">{title}</h2>
              <p className="text-muted-foreground font-body mb-6">{body}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild data-testid="view-orders-button">
                  <Link to="/dashboard?section=orders">View My Orders</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/checkout">Return to Checkout</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Success: full Thank You + Invoice ──
  const tx = transaction || {};
  const orderNumber = tx.tagada_order_number || tx.session_id;
  const manualPending = status === 'manual_pending';
  const heroTitle = manualPending
    ? "We've received your order"
    : 'Thank you for your order';
  const heroBody = manualPending ? (
    <>
      A secure payment link will be emailed to <span className="text-slate-900 font-medium">{tx.email || 'you'}</span> shortly
      to complete your purchase. Your items are reserved while we send it.
    </>
  ) : (
    <>
      We've sent a confirmation to <span className="text-slate-900 font-medium">{tx.email || 'your email'}</span>.
      Your invoice is below — keep it for your records.
    </>
  );
  return (
    <div className="bg-slate-50/40 min-h-[calc(100vh-200px)]">
      <SEO title={manualPending ? 'Order Received' : 'Thank You for Your Order'} noIndex />

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-4 pt-12 pb-8 text-center print:hidden" data-testid="payment-success-page">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 border border-emerald-200 mb-5">
          <CheckCircle className="h-9 w-9 text-emerald-600" data-testid={manualPending ? 'manual-order-received' : 'payment-confirmed'} />
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading text-slate-900 mb-3">{heroTitle}</h1>
        <p className="text-base text-slate-600 font-body max-w-lg mx-auto">{heroBody}</p>
        {manualPending && (
          <div
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-1.5 text-xs font-medium text-amber-800 tracking-wide"
            data-testid="manual-payment-pending-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
            Payment link en route
          </div>
        )}
      </div>

      {/* Invoice card */}
      <div className="max-w-3xl mx-auto px-4 pb-12">
        <Card className="border border-slate-200 shadow-sm print:shadow-none print:border-0" data-testid="invoice-card">
          <CardContent className="p-6 sm:p-10 space-y-8">
            <OrderHeader
              orderNumber={orderNumber}
              paidAt={tx.paid_at || tx.created_at}
              status={tx.status}
              manualPending={manualPending}
            />

            {/* Addresses */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <AddressBlock title="Ship To" address={tx.shipping_address} icon={MapPin} />
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">
                  <Truck className="h-3.5 w-3.5" />
                  Delivery
                </div>
                <p className="text-sm text-slate-700 font-body">{tx.shipping_label || 'Standard Shipping'}</p>
                {tx.customer_name && (
                  <p className="text-xs text-slate-500 mt-2">For {tx.customer_name}</p>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">
                <Receipt className="h-3.5 w-3.5" /> Items Ordered
              </div>
              <ItemsTable items={tx.items} />
            </div>

            {/* Totals */}
            <div className="max-w-sm ml-auto">
              <TotalsBlock tx={tx} manualPending={manualPending} />
            </div>

            {/* RUO footer (always shown on invoice — useful when printed) */}
            <div className="pt-6 border-t border-slate-200 text-[10px] leading-relaxed text-slate-400 font-body">
              All Wilvada LC products are intended <strong className="text-slate-500">strictly for in-vitro and pre-clinical research</strong>.
              Not for human or veterinary use. By purchasing, the customer certifies they are a qualified professional
              with proper facilities and assumes full responsibility for safe handling and regulatory compliance.
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8 print:hidden">
          <Button
            onClick={() => window.print()}
            data-testid="print-invoice-button"
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Printer className="h-4 w-4 mr-2" /> Print Invoice
          </Button>
          <Button asChild variant="outline" data-testid="view-orders-button">
            <Link to="/dashboard?section=orders"><Package className="h-4 w-4 mr-2" /> View All Orders</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/catalog">Continue Shopping</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
